import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom'
import { ProtectedRoute } from '@components/auth/ProtectedRoute'
import { PublicOnlyRoute } from '@components/auth/PublicOnlyRoute'
import { MainLayout } from '@components/layout/MainLayout'
import { AuthLayout } from '@components/layout/AuthLayout'

// Pages
import BadgesPage from '@pages/BadgesPage'
import LeaderboardPage from '@pages/LeaderboardPage'
import CollectionsPage from '@pages/CollectionsPage'
import LoginPage from '@features/auth/pages/LoginPage'
import RegisterPage from '@features/auth/pages/RegisterPage'
import MfaVerifyPage from '@features/auth/pages/MfaVerifyPage'
import ForgotPasswordPage from '@features/auth/pages/ForgotPasswordPage'
import VerifyResetCodePage from '@features/auth/pages/VerifyResetCodePage'
import ResetPasswordPage from '@features/auth/pages/ResetPasswordPage'
import { SearchResultsPage } from '@features/search/pages/SearchResultsPage'
import { AdvancedSearchPage } from '@features/search/pages/AdvancedSearchPage'
import { GroupsPage } from '@features/social/pages/GroupsPage'
import { GroupDetailsPage } from '@features/social/pages/GroupDetailsPage'
import { ShareCodePage } from '@features/social/pages/ShareCodePage'
import ProfilePage from '@features/profile/pages/ProfilePage'
import ProfileEditPage from '@features/profile/pages/ProfileEditPage'
import SettingsPage from '@features/profile/pages/SettingsPage'
import AdminDashboard from '@features/admin/pages/AdminDashboard'
import AdminAdventuresPage from '@features/admin/pages/AdminAdventuresPage'
import AdminUsersPage from '@features/admin/pages/AdminUsersPage'
import AdminThemesPage from '@features/admin/pages/AdminThemesPage'
import SubscriptionPage from '@features/subscriptions/pages/SubscriptionPage'
import MyRewardsPage from '@features/rewards/pages/MyRewardsPage'
import RewardDetailPage from '@features/rewards/pages/RewardDetailPage'
import GroupRewardTemplatesPage from '@features/rewards/pages/GroupRewardTemplatesPage'
import GroupRewardTemplateEditPage from '@features/rewards/pages/GroupRewardTemplateEditPage'
import GroupRewardTemplateStatsPage from '@features/rewards/pages/GroupRewardTemplateStatsPage'
import MediaManagerPage from '@features/media/pages/MediaManagerPage'
import CreateTicketPage from '@features/tickets/pages/CreateTicketPage'
import MyTicketsPage from '@features/tickets/pages/MyTicketsPage'
import TicketDetailPage from '@features/tickets/pages/TicketDetailPage'
import ModeratorTicketsPage from '@features/tickets/pages/ModeratorTicketsPage'
import ModeratorTicketDetailPage from '@features/tickets/pages/ModeratorTicketDetailPage'
import AdminTicketStatisticsPage from '@features/tickets/pages/AdminTicketStatisticsPage'
import LogsPage from '@features/logs/pages/LogsPage'
import StatisticsPage from '@features/statistics/pages/StatisticsPage'
import UnauthorizedPage from '@pages/UnauthorizedPage'
import NotFoundPage from '@pages/NotFoundPage'

// Layout wrapper components to prevent remounting on navigation
function ProtectedMainLayout() {
  return (
    <ProtectedRoute>
      <MainLayout>
        <Outlet />
      </MainLayout>
    </ProtectedRoute>
  )
}

function ProtectedAdminLayout() {
  return (
    <ProtectedRoute requiredRoles={['Admin']}>
      <MainLayout>
        <Outlet />
      </MainLayout>
    </ProtectedRoute>
  )
}

function PublicAuthLayout() {
  return (
    <PublicOnlyRoute>
      <AuthLayout>
        <Outlet />
      </AuthLayout>
    </PublicOnlyRoute>
  )
}

const router = createBrowserRouter([
  // Share code routes - public so they can handle auth redirects with referral codes
  {
    path: '/code/:shareCode',
    element: <ShareCodePage />,
  },
  {
    path: '/code',
    element: <ShareCodePage />,
  },
  // Public auth routes (login, register, etc.)
  {
    element: <PublicAuthLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/verify-reset-code', element: <VerifyResetCodePage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
    ],
  },
  // MFA verify (no PublicOnlyRoute - user might be partially authenticated)
  {
    path: '/mfa-verify',
    element: (
      <AuthLayout>
        <MfaVerifyPage />
      </AuthLayout>
    ),
  },
  // Admin routes (root level since this is the admin portal)
  {
    element: <ProtectedAdminLayout />,
    children: [
      { path: '/', element: <AdminDashboard /> },
      { path: '/adventures', element: <AdminAdventuresPage /> },
      { path: '/users', element: <AdminUsersPage /> },
      { path: '/themes', element: <AdminThemesPage /> },
      { path: '/tickets/statistics', element: <AdminTicketStatisticsPage /> },
      { path: '/statistics', element: <StatisticsPage /> },
      { path: '/logs', element: <LogsPage /> },
    ],
  },
  // Main protected routes with MainLayout
  {
    element: <ProtectedMainLayout />,
    children: [
      { path: '/badges', element: <BadgesPage /> },
      { path: '/leaderboard', element: <LeaderboardPage /> },
      { path: '/collections', element: <CollectionsPage /> },
      { path: '/groups', element: <GroupsPage /> },
      { path: '/groups/:id', element: <GroupDetailsPage /> },
      { path: '/groups/:id/rewards', element: <GroupRewardTemplatesPage /> },
      { path: '/groups/:id/rewards/new', element: <GroupRewardTemplateEditPage /> },
      { path: '/groups/:id/rewards/:templateId/edit', element: <GroupRewardTemplateEditPage /> },
      { path: '/groups/:id/rewards/:templateId/stats', element: <GroupRewardTemplateStatsPage /> },
      { path: '/media', element: <MediaManagerPage /> },
      { path: '/search', element: <SearchResultsPage /> },
      { path: '/search/advanced', element: <AdvancedSearchPage /> },
      { path: '/profile', element: <ProfilePage /> },
      { path: '/profile/:userId', element: <ProfilePage /> },
      { path: '/profile/edit', element: <ProfileEditPage /> },
      { path: '/settings', element: <SettingsPage /> },
      { path: '/subscription', element: <SubscriptionPage /> },
      { path: '/rewards', element: <MyRewardsPage /> },
      { path: '/rewards/:instanceId', element: <RewardDetailPage /> },
      { path: '/support/new', element: <CreateTicketPage /> },
      { path: '/support/tickets', element: <MyTicketsPage /> },
      { path: '/support/tickets/:ticketId', element: <TicketDetailPage /> },
      { path: '/tickets', element: <ModeratorTicketsPage /> },
      { path: '/tickets/:ticketId', element: <ModeratorTicketDetailPage /> },
      { path: '/unauthorized', element: <UnauthorizedPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])

export function AppRouter() {
  return <RouterProvider router={router} />
}
